import { AIAgentInsights, CRMChannel } from "@jugl-web/rest-api";
import { useAppVariant, useTranslations } from "@jugl-web/utils";
import { FC, SVGProps, useMemo } from "react";
import { ReactComponent as InstagramIcon } from "../assets/instagram.svg";
import { ReactComponent as TwilioIcon } from "../assets/twilio.svg";
import { ReactComponent as WebChatIcon } from "../assets/web-chat.svg";
import { ReactComponent as WhatsappIcon } from "../assets/whatsapp.svg";
import { ReactComponent as EmailIcon } from "../assets/email.svg";
import { ReactComponent as VoiceIcon } from "../assets/voice.svg";

export interface ChannelSummary {
  channel: CRMChannel;
  icon: FC<SVGProps<SVGSVGElement>>;
  name: string;
  totalChats: number;
  totalMessages: number;
  aiAgentChats: number;
  aiAgentMessages: number;
  humanChats: number;
  humanMessages: number;
}

export const useChatsOverviewChannelSummaries = (
  insightsByChannel: Partial<Record<CRMChannel, AIAgentInsights | undefined>>
): ChannelSummary[] => {
  const { t } = useTranslations();
  const { isMobile } = useAppVariant();

  return useMemo(() => {
    const channelConfig: {
      channel: CRMChannel;
      icon: FC<SVGProps<SVGSVGElement>>;
      name: string;
      className?: string;
    }[] = [
      {
        channel: CRMChannel.whatsapp,
        icon: WhatsappIcon,
        name: t({ id: "cx-analytics.whatsapp", defaultMessage: "WhatsApp" }),
        className: isMobile
          ? "bg-gradient-to-r from-[#F4FAF4] to-[#FDFDFD]"
          : undefined,
      },
      {
        channel: CRMChannel.instagram,
        icon: InstagramIcon,
        name: t({ id: "cx-analytics.instagram", defaultMessage: "Instagram" }),
        className: isMobile
          ? "bg-gradient-to-r from-[#FAF4F6] to-[#FDFDFD]"
          : undefined,
      },
      {
        channel: CRMChannel.chatbot,
        icon: WebChatIcon,
        name: t({ id: "cx-analytics.web-chat", defaultMessage: "Web Chat" }),
        className: isMobile
          ? "bg-gradient-to-r from-[#F4F7FA] to-[#FDFDFD]"
          : undefined,
      },
      {
        channel: CRMChannel.twilio,
        icon: TwilioIcon,
        name: t({
          id: "cx-analytics.sms-text-chat",
          defaultMessage: "SMS (Text) Chat",
        }),
        className: isMobile
          ? "bg-gradient-to-r from-[#F4F9FA] to-[#FDFDFD]"
          : undefined,
      },
      {
        channel: CRMChannel.email,
        icon: EmailIcon,
        name: t({
          id: "cx-analytics.email-chat",
          defaultMessage: "Email Chat",
        }),
        className: isMobile
          ? "bg-gradient-to-r from-[#F4F9FA] to-[#FDFDFD]"
          : undefined,
      },
      {
        channel: CRMChannel.voice,
        icon: VoiceIcon,
        name: t({
          id: "cx-analytics.phone-calls",
          defaultMessage: "Phone Calls",
        }),
        className: isMobile
          ? "bg-gradient-to-r from-[#F5F4FA] to-[#FEFBFF]"
          : undefined,
      },
    ];

    return channelConfig.map(({ channel, icon, name, className }) => {
      const insights = insightsByChannel[channel];
      return {
        channel,
        icon,
        name,
        className,
        totalChats: insights?.total_conversations ?? 0,
        totalMessages:
          (insights?.ai_replies ?? 0) + (insights?.human_sent_messages ?? 0),
        aiAgentChats: insights?.resolved ?? 0,
        aiAgentMessages: insights?.ai_replies ?? 0,
        humanChats: insights?.unresolved ?? 0,
        humanMessages: insights?.human_sent_messages ?? 0,
      };
    });
  }, [t, isMobile, insightsByChannel]);
};
